import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { MatchPrediction, PredictionWinner } from "@/types/app";
import { directusService } from "@/services/directus";
import { Loader2, Trophy, ChevronLeft, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const PredictionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [matches, setMatches] = useState<MatchPrediction[]>([]);
  const [winners, setWinners] = useState<PredictionWinner[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<MatchPrediction | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [loadingWinners, setLoadingWinners] = useState(true);
  const [team1Score, setTeam1Score] = useState<number>(0);
  const [team2Score, setTeam2Score] = useState<number>(0);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [submittedMatches, setSubmittedMatches] = useState<string[]>([]);
  const [showNotification, setShowNotification] = useState<{
    message: string;
    type: "error" | "success";
  } | null>(null);

  useEffect(() => {
    loadMatches();
    loadWinners();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const activeMatches = await directusService.getActiveMatches();
      setMatches(activeMatches);

      // Charger les matchs déjà soumis
      const submitted = JSON.parse(
        localStorage.getItem("submittedPredictions") || "[]"
      );
      setSubmittedMatches(submitted);
    } catch (error) {
      console.error("Error loading matches:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadWinners = async () => {
    try {
      setLoadingWinners(true);
      const winnersData = await directusService.getPredictionWinners();
      setWinners(winnersData);
    } catch (error) {
      console.error("Error loading winners:", error);
    } finally {
      setLoadingWinners(false);
    }
  };

  const handlePredictionSubmit = async () => {
    if (!selectedMatch) return;

    // Validation du pseudo
    if (!userName || userName.trim().length < 2) {
      setShowNotification({
        message: "Veuillez entrer un pseudo valide (minimum 2 caractères)",
        type: "error",
      });
      return;
    }

    // Validation du numéro de téléphone
    if (!phoneNumber || phoneNumber.length < 8) {
      setShowNotification({
        message: "Veuillez entrer un numéro de téléphone valide",
        type: "error",
      });
      return;
    }

    try {
      setSubmitting(true);

      const predictionData = {
        matchId: selectedMatch.id!,
        prediction: `${team1Score}-${team2Score}`,
        team1Score,
        team2Score,
        phoneNumber,
        userName: userName.trim(),
        status: "published" as const,
      };

      await directusService.submitPrediction(predictionData);

      // Marquer le match comme soumis dans localStorage
      const submitted = JSON.parse(
        localStorage.getItem("submittedPredictions") || "[]"
      );
      submitted.push(selectedMatch.id);
      localStorage.setItem("submittedPredictions", JSON.stringify(submitted));
      setSubmittedMatches(submitted);

      // Afficher le succès
      setShowNotification({
        message: `Votre pronostic de ${team1Score}-${team2Score} a été enregistré avec succès !`,
        type: "success",
      });

      // Réinitialiser et revenir à la liste
      setTeam1Score(0);
      setTeam2Score(0);
      setPhoneNumber("");
      setUserName("");
      setSelectedMatch(null);
    } catch (error: any) {
      console.error("❌ Erreur complète:", error);

      const errorMessage =
        error.response?.data?.errors?.[0]?.message ||
        error.response?.data?.message ||
        error.message ||
        "Erreur lors de l'enregistrement du pronostic";

      setShowNotification({
        message: errorMessage,
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Notification component
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#002366]" />
          <p className="mt-4 text-gray-600">Chargement des matchs...</p>
        </div>
      </div>
    );
  }

  // Si un match est sélectionné, afficher le formulaire de pronostic
  if (selectedMatch) {
    const match = selectedMatch;
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Notification */}
        {showNotification && (
          <div
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
              showNotification.type === "success"
                ? "bg-green-500 text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {showNotification.message}
          </div>
        )}

        {/* Header */}
        <div className="bg-[#002366] p-4 sm:p-6 text-white pb-6">
          <button
            onClick={() => {
              setSelectedMatch(null);
              setTeam1Score(0);
              setTeam2Score(0);
              setPhoneNumber("");
              setUserName("");
            }}
            className="mb-4 rounded-full bg-white hover:bg-white/90 p-2 transition-all inline-flex items-center"
          >
            <ChevronLeft className="h-6 w-6 text-[#002366]" />
          </button>

          <div className="flex items-center justify-center mb-3">
            <Trophy className="h-8 w-8 mr-2" />
            <h1 className="text-2xl sm:text-3xl font-bold">{match.title}</h1>
          </div>
          <p className="text-center text-white/90 text-sm">
            {match.description}
          </p>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-4 sm:p-6">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            {/* Match Display */}
            <div className="flex items-center justify-between mb-8">
              {/* Équipe 1 */}
              <div className="flex flex-col items-center flex-1">
                <div className="relative w-20 h-20 sm:w-28 sm:h-28 mb-3 rounded-full overflow-hidden border-4 border-gray-200 shadow-lg">
                  <img
                    src={match.firstTeamImageUrl}
                    alt={match.firstTeamName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://via.placeholder.com/100?text=Team1";
                    }}
                  />
                </div>
                <h3 className="text-lg font-bold text-center">
                  {match.firstTeamName}
                </h3>
              </div>

              {/* VS */}
              <div className="px-4">
                <div className="text-3xl font-bold text-gray-400">VS</div>
              </div>

              {/* Équipe 2 */}
              <div className="flex flex-col items-center flex-1">
                <div className="relative w-20 h-20 sm:w-28 sm:h-28 mb-3 rounded-full overflow-hidden border-4 border-gray-200 shadow-lg">
                  <img
                    src={match.secondTeamImageUrl}
                    alt={match.secondTeamName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://via.placeholder.com/100?text=Team2";
                    }}
                  />
                </div>
                <h3 className="text-lg font-bold text-center">
                  {match.secondTeamName}
                </h3>
              </div>
            </div>

            {/* Score Selection */}
            <div className="space-y-4">
              <p className="text-center text-sm text-gray-600 mb-4">
                Entrez le score final :
              </p>

              {/* Score Display */}
              <div className="flex items-center justify-center gap-4 mb-6">
                {/* Team 1 Score */}
                <div className="flex flex-col items-center flex-1 max-w-[140px]">
                  <p className="text-sm font-semibold text-gray-700 mb-3 text-center truncate w-full">
                    {match.firstTeamName}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full p-0"
                      onClick={() => setTeam1Score(Math.max(0, team1Score - 1))}
                      disabled={team1Score === 0}
                    >
                      -
                    </Button>
                    <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-xl border-2 border-blue-500">
                      <span className="text-3xl font-bold text-blue-600">
                        {team1Score}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full p-0"
                      onClick={() => setTeam1Score(Math.min(9, team1Score + 1))}
                      disabled={team1Score === 9}
                    >
                      +
                    </Button>
                  </div>
                </div>

                {/* Separator */}
                <div className="text-4xl font-bold text-gray-400 mt-8">-</div>

                {/* Team 2 Score */}
                <div className="flex flex-col items-center flex-1 max-w-[140px]">
                  <p className="text-sm font-semibold text-gray-700 mb-3 text-center truncate w-full">
                    {match.secondTeamName}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full p-0"
                      onClick={() => setTeam2Score(Math.max(0, team2Score - 1))}
                      disabled={team2Score === 0}
                    >
                      -
                    </Button>
                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-xl border-2 border-green-500">
                      <span className="text-3xl font-bold text-green-600">
                        {team2Score}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 rounded-full p-0"
                      onClick={() => setTeam2Score(Math.min(9, team2Score + 1))}
                      disabled={team2Score === 9}
                    >
                      +
                    </Button>
                  </div>
                </div>
              </div>

              {/* Quick Score Buttons */}
              <div className="space-y-2">
                <p className="text-center text-xs text-gray-500">
                  Scores fréquents :
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    [1, 0],
                    [2, 0],
                    [2, 1],
                    [3, 0],
                    [0, 0],
                    [1, 1],
                    [2, 2],
                    [3, 3],
                    [0, 1],
                    [0, 2],
                    [1, 2],
                    [0, 3],
                  ].map(([s1, s2]) => (
                    <Button
                      key={`${s1}-${s2}`}
                      variant="outline"
                      size="sm"
                      className={`h-9 text-sm ${
                        team1Score === s1 && team2Score === s2
                          ? "!bg-[#002366] !text-white !border-[#002366] hover:!bg-[#001a4d]"
                          : ""
                      }`}
                      onClick={() => {
                        setTeam1Score(s1);
                        setTeam2Score(s2);
                      }}
                    >
                      {s1}-{s2}
                    </Button>
                  ))}
                </div>
              </div>

              {/* User Name Input */}
              <div className="space-y-2 mt-4">
                <label className="block text-sm font-semibold text-gray-700 text-center">
                  Votre pseudo
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Ex: JohnDoe"
                  maxLength={30}
                  className="w-full h-12 px-4 text-center text-lg font-semibold border-2 border-gray-300 rounded-xl focus:border-[#002366] focus:outline-none focus:ring-2 focus:ring-[#002366]/20 transition-all"
                />
                <p className="text-xs text-gray-500 text-center">
                  Votre nom de joueur
                </p>
              </div>

              {/* Phone Number Input */}
              <div className="space-y-2 mt-4">
                <label className="block text-sm font-semibold text-gray-700 text-center">
                  Votre numéro de téléphone
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="Ex: 70123456"
                  maxLength={8}
                  className="w-full h-12 px-4 text-center text-lg font-semibold border-2 border-gray-300 rounded-xl focus:border-[#002366] focus:outline-none focus:ring-2 focus:ring-[#002366]/20 transition-all"
                />
                <p className="text-xs text-gray-500 text-center">
                  Pour vous contacter en cas de gain
                </p>
              </div>

              {/* Submit Button */}
              <Button
                className="w-full h-12 text-lg font-semibold mt-4 bg-[#002366] hover:bg-[#001a4d]"
                disabled={submitting}
                onClick={handlePredictionSubmit}
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  `Confirmer le score ${team1Score}-${team2Score}`
                )}
              </Button>
            </div>

            {match.matchDate && (
              <p className="text-center text-sm text-gray-500 mt-4">
                Date du match:{" "}
                {new Date(match.matchDate).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Vue principale avec la liste des matchs et les gagnants
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {showNotification && (
        <div
          className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
            showNotification.type === "success"
              ? "bg-green-500 text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {showNotification.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-[#002366] p-4 sm:p-6 text-white">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 rounded-full bg-white hover:bg-white/90 p-2 transition-all inline-flex items-center"
        >
          <ChevronLeft className="h-6 w-6 text-[#002366]" />
        </button>

        <div className="flex items-center justify-center mb-2">
          <Trophy className="h-8 w-8 mr-2" />
          <h1 className="text-2xl sm:text-3xl font-bold">Pronostiques</h1>
        </div>
        <p className="text-center text-white/90 text-sm">
          Cliquez sur un match pour faire votre pronostic et tentez de gagner !
        </p>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Section: Matchs disponibles */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Trophy className="h-6 w-6 mr-2 text-[#002366]" />
            Matchs disponibles
          </h2>

          {matches.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow">
              <p className="text-gray-500">
                Aucun match disponible pour le moment.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((match) => {
                const isSubmitted = submittedMatches.includes(match.id || "");
                return (
                  <button
                    key={match.id}
                    onClick={() => {
                      if (!isSubmitted) {
                        setSelectedMatch(match);
                      }
                    }}
                    disabled={isSubmitted}
                    className={`w-full p-4 rounded-xl border-2 transition-all ${
                      isSubmitted
                        ? "bg-gray-100 border-gray-300 cursor-not-allowed opacity-60"
                        : "bg-white border-[#002366] hover:bg-blue-50 hover:shadow-lg cursor-pointer"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      {/* Équipe 1 */}
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-16 h-16 mb-2 rounded-full overflow-hidden border-2 border-gray-200">
                          <img
                            src={match.firstTeamImageUrl}
                            alt={match.firstTeamName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                "https://via.placeholder.com/64?text=T1";
                            }}
                          />
                        </div>
                        <p className="text-sm font-semibold text-center px-1">
                          {match.firstTeamName}
                        </p>
                      </div>

                      {/* VS */}
                      <div className="px-4">
                        <div className="text-xl font-bold text-gray-400">
                          VS
                        </div>
                      </div>

                      {/* Équipe 2 */}
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-16 h-16 mb-2 rounded-full overflow-hidden border-2 border-gray-200">
                          <img
                            src={match.secondTeamImageUrl}
                            alt={match.secondTeamName}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src =
                                "https://via.placeholder.com/64?text=T2";
                            }}
                          />
                        </div>
                        <p className="text-sm font-semibold text-center px-1">
                          {match.secondTeamName}
                        </p>
                      </div>
                    </div>

                    {/* Infos du match */}
                    <div className="mt-3 text-center">
                      <p className="text-sm font-semibold text-[#002366]">
                        {match.title}
                      </p>
                      {match.matchDate && (
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(match.matchDate).toLocaleDateString(
                            "fr-FR",
                            {
                              day: "numeric",
                              month: "long",
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </p>
                      )}
                    </div>

                    {/* Badge si déjà soumis */}
                    {isSubmitted && (
                      <div className="mt-2 inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                        ✓ Pronostic enregistré
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Section: Gagnants des pronostiques passés */}
        <section>
          <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Award className="h-6 w-6 mr-2 text-yellow-500" />
            Gagnants des pronostiques passés
          </h2>

          {loadingWinners ? (
            <div className="bg-white rounded-xl p-8 text-center shadow">
              <Loader2 className="h-8 w-8 animate-spin text-[#002366] mx-auto mb-2" />
              <p className="text-gray-500">Chargement des gagnants...</p>
            </div>
          ) : winners.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow">
              <p className="text-gray-500">Aucun gagnant pour le moment.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#002366] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm sm:text-base font-semibold">
                        Prix
                      </th>
                      <th className="px-6 py-4 text-left text-sm sm:text-base font-semibold">
                        Pseudo
                      </th>
                      <th className="px-6 py-4 text-left text-sm sm:text-base font-semibold">
                        Score
                      </th>
                      <th className="px-6 py-4 text-left text-sm sm:text-base font-semibold">
                        Match
                      </th>
                      <th className="px-6 py-4 text-left text-sm sm:text-base font-semibold">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {winners.map((winner, index) => (
                      <tr
                        key={winner.id || index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-5">
                          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-bold text-sm sm:text-base whitespace-nowrap">
                            {winner.prix}
                          </span>
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center gap-2">
                            <Award className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                            <span className="font-semibold text-gray-800 text-sm sm:text-base">
                              {winner.userName}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full font-bold text-sm sm:text-base whitespace-nowrap">
                            {winner.score}
                          </span>
                        </td>
                        <td className="px-6 py-5 text-sm sm:text-base text-gray-600">
                          {winner.match?.firstTeamName &&
                          winner.match?.secondTeamName
                            ? `${winner.match.firstTeamName} vs ${winner.match.secondTeamName}`
                            : "Match terminé"}
                        </td>
                        <td className="px-6 py-5 text-sm sm:text-base text-gray-500">
                          {winner.dateCreated
                            ? new Date(winner.dateCreated).toLocaleDateString(
                                "fr-FR",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }
                              )
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
